import { Constructor, getId } from '@cawita/core-front';
import { CrudService, CwtApiService, mapApiDataArray, mapApiDataItem, mapPaginatedToArray, PaginatedResponse, QueryFilters, RequestConfig, SuccessResponse } from '@cawita/core-front/api';
import { Observable } from 'rxjs';
import { map, pluck } from 'rxjs/operators';
import { mapArrayToPaginated } from '../../utils/rxjs.utils';

export interface GetPaginatedResponse<D> {
    data: D[];
    page: number;
    total: number;
    limit: number;
}

export class GenericCrudService<D> implements CrudService<D> {
    constructor(
        protected api: CwtApiService,
        protected path: string,
        protected cnstrctr: Constructor<D>
    ) { }

    getOne(id: D | string, config?: RequestConfig): Observable<D> {
        if (typeof id !== 'string') id = getId(id);
        return this.api.get(`${this.path}/${id}`, {}, config).pipe(mapApiDataItem(this.cnstrctr));
    }

    getAll(filters?: QueryFilters, config?: RequestConfig): Observable<D[] | PaginatedResponse<D>> {
        return this.api.get(this.path, filters, config).pipe(mapApiDataArray(this.cnstrctr));
    }

    getFirst(filters?: QueryFilters, config?: RequestConfig): Observable<D | null> {
        return this.getAllPaginated(filters, {
            ...(config ?? {}),
            options: {
                ...(config?.options ?? {}),
                limit: 1
            }
        }).pipe(map(res => res?.data?.[0] ?? null));
    }

    getAllPaginated(filters?: QueryFilters, config?: RequestConfig): Observable<PaginatedResponse<D>> {
        return this.getAll(filters, {
            ...(config ?? {}),
            options: {
                ...(config?.options ?? {}),
                doPaginate: true,
            }
        }).pipe(mapArrayToPaginated());
    }

    getAllNoPaginate(filters?: QueryFilters, config?: RequestConfig): Observable<D[]> {
        return this.getAll(filters, {
            ...(config ?? {}),
            options: {
                ...(config?.options ?? {})
            }
        }).pipe(mapPaginatedToArray());
    }

    create(body: D | Partial<D>, config?: RequestConfig): Observable<D> {
        return this.api.post(this.path, body, config).pipe(mapApiDataItem(this.cnstrctr));
    }

    update(id: D | string, body: D | Partial<D>, config?: RequestConfig): Observable<D> {
        if (typeof id !== 'string') id = getId(id);
        return this.api.patch(`${this.path}/${id}`, body, config).pipe(mapApiDataItem(this.cnstrctr));
    }

    delete(id: D | string, config?: RequestConfig): Observable<SuccessResponse> {
        if (typeof id !== 'string') id = getId(id);
        return this.api.delete(`${this.path}/${id}`, config);
    }
}
